import React, { useEffect, useRef, useState } from 'react';
import siteAnalyticsService from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, faDecimal, decodePath } from './useDateRange';
import './analytics.css';
import { WEB_BASE_URL } from '../../config/env';

// دامنه‌ی عمومی برای ساختن لینکِ قابل‌کلیکِ محصول/دسته/فروشگاه.
// قابل override با env در بیلد؛ در config/constants.ts معادل عمومی وجود ندارد (فقط API_BASE_URL).
const WEB_BASE = WEB_BASE_URL;
const toman = (n?: number) => `${fa(n || 0)} تومان`;

const SiteAnalyticsMarketplace: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getMarketplaceInsights(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  const rev = data?.revenue || { orders: 0, revenue: 0, avgOrderValue: 0 };
  const cart = data?.cartAbandonment || { withCart: 0, converted: 0, abandoned: 0, abandonmentRatePct: 0 };
  const search = data?.searchInsights || { totalSearches: 0, resultClicks: 0, clickThroughPct: 0, topQueries: [], zeroResultQueries: [] };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>بینش‌های مارکت‌پلیس</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}
      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {data && (
        <>
          {/* KPI: درآمد واقعی + رهاسازی سبد */}
          <div className="analytics-kpi-grid">
            <div className="analytics-kpi-card green">
              <div className="accent" />
              <div className="label">درآمد (سفارش‌های پرداخت‌شده)</div>
              <div className="value">{toman(rev.revenue)}</div>
            </div>
            <div className="analytics-kpi-card">
              <div className="accent" />
              <div className="label">تعداد سفارش</div>
              <div className="value">{fa(rev.orders)}</div>
            </div>
            <div className="analytics-kpi-card teal">
              <div className="accent" />
              <div className="label">میانگین ارزش سفارش</div>
              <div className="value">{toman(rev.avgOrderValue)}</div>
            </div>
            <div className="analytics-kpi-card red">
              <div className="accent" />
              <div className="label">نرخ رهاسازی سبد</div>
              <div className="value">{fa(cart.abandonmentRatePct)}٪</div>
              <span className="change flat">
                {fa(cart.abandoned)} رهاشده از {fa(cart.withCart)} سبد
              </span>
            </div>
          </div>

          {/* پربازدیدترین محصولات — با نام و لینک واقعی */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>پربازدیدترین محصولات</h3>
            </div>
            {!data.mostViewedProducts || data.mostViewedProducts.length === 0 ? (
              <div className="analytics-empty">داده‌ی product_view کافی نیست</div>
            ) : (
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>محصول</th>
                    <th>قیمت</th>
                    <th>بازدید</th>
                    <th>سشن‌ها</th>
                    <th>افزودن به سبد</th>
                    <th>نرخ سبد</th>
                    <th>خرید</th>
                    <th>درآمد</th>
                  </tr>
                </thead>
                <tbody>
                  {data.mostViewedProducts.map((p: any) => (
                    <tr key={p.productId || ''}>
                      <td>
                        {p.name ? (
                          <a
                            href={`${WEB_BASE}${p.url || `/products/${p.slug || ''}`}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit' }}
                          >
                            {p.thumbnail && (
                              <img
                                src={p.thumbnail}
                                alt=""
                                width={36}
                                height={36}
                                style={{ borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                              />
                            )}
                            <span>{p.name}</span>
                          </a>
                        ) : (
                          <span
                            title="محصول حذف‌شده یا یافت‌نشده"
                            style={{ direction: 'ltr', fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-secondary)' }}
                          >
                            {p.productId}
                          </span>
                        )}
                      </td>
                      <td>{p.price ? toman(p.price) : '—'}</td>
                      <td>{fa(p.views)}</td>
                      <td>{fa(p.sessionCount)}</td>
                      <td>{fa(p.addToCart)}</td>
                      <td>
                        <strong style={{ color: p.cartConversionPct >= 10 ? 'var(--color-success-dark)' : undefined }}>
                          {faDecimal(p.cartConversionPct || 0, 1)}٪
                        </strong>
                      </td>
                      {/* 🆕 خرید/درآمد واقعی از سفارش‌های PAID */}
                      <td>
                        {p.purchases ? (
                          <strong style={{ color: 'var(--color-success-dark)' }}>{fa(p.purchases)}</strong>
                        ) : (
                          '—'
                        )}
                        {typeof p.buyConversionPct === 'number' && p.buyConversionPct > 0 && (
                          <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}> ({faDecimal(p.buyConversionPct, 1)}٪)</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{p.purchaseRevenue ? toman(p.purchaseRevenue) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* جستجو */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>جستجوی سایت</h3>
            </div>
            <div className="analytics-kpi-grid" style={{ marginBottom: 16 }}>
              <div className="analytics-kpi-card">
                <div className="accent" />
                <div className="label">کل جستجوها</div>
                <div className="value">{fa(search.totalSearches)}</div>
              </div>
              <div className="analytics-kpi-card purple">
                <div className="accent" />
                <div className="label">کلیک روی نتیجه</div>
                <div className="value">{fa(search.resultClicks)}</div>
              </div>
              <div className="analytics-kpi-card orange">
                <div className="accent" />
                <div className="label">نرخ کلیک (CTR)</div>
                <div className="value">{fa(search.clickThroughPct)}٪</div>
              </div>
            </div>
            <div className="analytics-grid-2col">
              <div>
                <h4 style={{ margin: '0 0 8px' }}>پرتکرارترین عبارات</h4>
                {!search.topQueries || search.topQueries.length === 0 ? (
                  <div className="analytics-empty">داده‌ای نیست</div>
                ) : (
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>عبارت</th>
                        <th>تعداد</th>
                        <th>کاربر</th>
                      </tr>
                    </thead>
                    <tbody>
                      {search.topQueries.map((q: any) => (
                        <tr key={q.query}>
                          <td>{q.query}</td>
                          <td>{fa(q.count)}</td>
                          <td>{fa(q.visitors)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div>
                <h4 style={{ margin: '0 0 8px' }}>جستجوهای بی‌نتیجه ⚠️</h4>
                {!search.zeroResultQueries || search.zeroResultQueries.length === 0 ? (
                  <div className="analytics-empty">جستجوی بی‌نتیجه‌ای ثبت نشده</div>
                ) : (
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>عبارت</th>
                        <th>تعداد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {search.zeroResultQueries.map((q: any) => (
                        <tr key={q.query}>
                          <td>{q.query}</td>
                          <td>{fa(q.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <div className="analytics-grid-2col">
            {/* دسته‌های پربازدید */}
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>دسته‌های پربازدید</h3>
              </div>
              {!data.topCategories || data.topCategories.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>دسته</th>
                      <th>بازدید</th>
                      <th>کاربر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topCategories.map((c: any) => (
                      <tr key={c.path}>
                        <td>
                          <a href={`${WEB_BASE}${c.path}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            {c.title || decodePath(c.path)}
                          </a>
                        </td>
                        <td>{fa(c.pageViews)}</td>
                        <td>{fa(c.visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ترافیک فروشگاه‌ها */}
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>فروشگاه‌های پربازدید</h3>
              </div>
              {!data.sellerTraffic || data.sellerTraffic.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>فروشگاه</th>
                      <th>بازدید</th>
                      <th>کاربر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sellerTraffic.map((s: any) => (
                      <tr key={s.path}>
                        <td>
                          <a href={`${WEB_BASE}${s.path}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            {s.title || decodePath(s.path)}
                          </a>
                        </td>
                        <td>{fa(s.pageViews)}</td>
                        <td>{fa(s.visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>بازدید محصول بر اساس منبع</h3>
              </div>
              {!data.productViewsBySource || data.productViewsBySource.length === 0 ? (
                <div className="analytics-empty">داده‌ی product_view ثبت نشده</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>منبع</th>
                      <th>بازدید</th>
                      <th>کاربر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.productViewsBySource.map((r: any) => (
                      <tr key={r.medium || ''}>
                        <td>{r.medium || '(نامشخص)'}</td>
                        <td>{fa(r.views)}</td>
                        <td>{fa(r.visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>پنل فروشنده vs مسیر خریدار</h3>
              </div>
              {!data.sellerVsBuyer || data.sellerVsBuyer.length === 0 ? (
                <div className="analytics-empty">داده‌ای نیست</div>
              ) : (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>نوع</th>
                      <th>بازدید</th>
                      <th>کاربر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sellerVsBuyer.map((r: any) => (
                      <tr key={r.kind}>
                        <td>{r.kind === 'seller_panel' ? '🏪 پنل فروشنده' : '🛒 مسیر خریدار'}</td>
                        <td>{fa(r.views)}</td>
                        <td>{fa(r.visitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SiteAnalyticsMarketplace;
